/**
 * InstallAppPrompt — eigen "App installeren"-banner.
 *
 * Probleem: Chrome/Edge tonen hun ingebouwde install-prompt maar één keer en
 * verbergen 'm wekenlang als de gebruiker hem wegklikt. Daardoor missen
 * medewerkers de kans om de PWA te installeren.
 *
 * Oplossing: we vangen het `beforeinstallprompt` event op, voorkomen de native
 * popup en tonen onze eigen banner. Die blijft elke sessie terugkomen zolang
 * de app nog niet is geïnstalleerd. Op iOS (geen beforeinstallprompt support)
 * tonen we een uitleg met instructies omdat installatie daar handmatig moet.
 */
import { useEffect, useState } from 'react';
import { X, Download, Smartphone } from 'lucide-react';

const SESSION_HIDE_KEY = 'install_banner_hidden_session';
const INSTALL_DETECTED_KEY = 'waszbase_installed_detected';

function isStandalone() {
  // PWA al geïnstalleerd & geopend vanaf beginscherm
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true ||
    document.referrer.startsWith('android-app://')
  );
}

function isIOS() {
  // iPhone, iPod
  if (/iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream) return true;
  // iPadOS 13+ identificeert zich als Macintosh — check op touch + Mac
  if (
    navigator.platform === 'MacIntel' &&
    typeof navigator.maxTouchPoints === 'number' &&
    navigator.maxTouchPoints > 1
  ) return true;
  return false;
}

export default function InstallAppPrompt({ user }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showIOSHelp, setShowIOSHelp] = useState(false);
  const [hiddenThisSession, setHiddenThisSession] = useState(
    sessionStorage.getItem(SESSION_HIDE_KEY) === '1'
  );

  // Detect: native install event
  useEffect(() => {
    if (isStandalone()) {
      localStorage.setItem(INSTALL_DETECTED_KEY, '1');
      return;
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // Wanneer de app daadwerkelijk geïnstalleerd wordt
    const installedHandler = () => {
      localStorage.setItem(INSTALL_DETECTED_KEY, '1');
      setShowBanner(false);
      setDeferredPrompt(null);
    };
    window.addEventListener('appinstalled', installedHandler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, []);

  // iOS-fallback: geen native event, dus tonen we de banner na 2s als
  // de app nog niet als PWA draait. Werkt voor iPhone én iPad
  // (iPadOS identificeert zich als Mac+touch). Bij Chrome op iOS wordt
  // Safari aanbevolen in de uitleg-popup.
  // NB: deze flow draait nu ook zonder ingelogde user, zodat medewerkers de
  // app al vanaf de loginpagina kunnen installeren.
  useEffect(() => {
    if (isStandalone()) return;
    if (localStorage.getItem(INSTALL_DETECTED_KEY) === '1') return;
    if (hiddenThisSession) return;
    if (isIOS()) {
      const t = setTimeout(() => setShowBanner(true), 2000);
      return () => clearTimeout(t);
    }
  }, [hiddenThisSession]);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      try {
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === 'accepted') {
          localStorage.setItem(INSTALL_DETECTED_KEY, '1');
        }
      } catch { /* genegeerd */ }
      setDeferredPrompt(null);
      setShowBanner(false);
    } else if (isIOS()) {
      setShowIOSHelp(true);
    }
  };

  const handleHide = () => {
    // Verberg alleen voor deze sessie — bij volgend bezoek/login komt 'ie terug
    sessionStorage.setItem(SESSION_HIDE_KEY, '1');
    setHiddenThisSession(true);
    setShowBanner(false);
  };

  if (!showBanner) return null;
  if (isStandalone()) return null;
  if (localStorage.getItem(INSTALL_DETECTED_KEY) === '1') return null;

  return (
    <>
      <div
        className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[10001] max-w-md w-[calc(100vw-2rem)] bg-white border-2 border-indigo-300 rounded-2xl shadow-2xl p-4 flex items-start gap-3"
        data-testid="install-prompt-banner"
      >
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center flex-shrink-0 text-white">
          <Smartphone className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-bold text-slate-900 text-sm">Installeer WaszBase</h4>
          <p className="text-xs text-slate-600 mt-1">
            Krijg sneller toegang en gebruik WaszBase als een echte app op je telefoon.
          </p>
          <div className="flex gap-2 mt-3">
            <button
              onClick={handleInstall}
              className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-3 py-2 rounded-lg flex items-center gap-1.5"
              data-testid="install-app-btn"
            >
              <Download className="w-4 h-4" /> Installeren
            </button>
            <button
              onClick={handleHide}
              className="text-slate-500 hover:text-slate-700 text-sm px-2"
              data-testid="install-later-btn"
            >
              Later
            </button>
          </div>
        </div>
        <button
          onClick={handleHide}
          className="text-slate-400 hover:text-slate-600 flex-shrink-0"
          aria-label="Sluiten"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* iOS-help dialoog: stap-voor-stap omdat iOS geen native install-event heeft */}
      {showIOSHelp && (
        <div className="fixed inset-0 z-[10010] bg-black/50 flex items-end sm:items-center justify-center p-4" onClick={() => setShowIOSHelp(false)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-bold text-lg mb-2">Installeer WaszBase op iPhone</h3>
            <ol className="space-y-3 text-sm text-slate-700 mt-3">
              <li><strong>1.</strong> Tik onderaan op het <strong>deel-icoon</strong> <span className="inline-block px-1.5 py-0.5 bg-slate-100 rounded">⎙</span> in Safari.</li>
              <li><strong>2.</strong> Scroll omlaag en kies <strong>"Zet op beginscherm"</strong>.</li>
              <li><strong>3.</strong> Tik rechtsboven op <strong>"Voeg toe"</strong>.</li>
            </ol>
            <p className="text-xs text-slate-500 mt-4 italic">Belangrijk: open eerst WaszBase in <strong>Safari</strong>, niet in Chrome — installatie via Chrome op iPhone werkt niet.</p>
            <button
              onClick={() => setShowIOSHelp(false)}
              className="mt-4 w-full bg-indigo-600 hover:bg-indigo-700 text-white py-2 rounded-lg font-medium"
              data-testid="ios-help-close"
            >
              Begrepen
            </button>
          </div>
        </div>
      )}
    </>
  );
}
